import { lazy, type FC } from 'react';
import Cookies from 'js-cookie';
import { IntlProvider } from 'react-intl';
import type { WithChildren } from '../helpers';

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig';

// Only these three are reachable: OpenSooqI18nProvider resolves every other locale to
// 'ar'. They are loaded one at a time so a page load ships the active translation
// file rather than all eight, and switching language reloads the page, so the locale
// is fixed for the lifetime of a load.
type SupportedLocale = 'ar' | 'en' | 'ku';

type MessageMap = Record<string, string>;

const messageLoaders: Record<SupportedLocale, () => Promise<{ default: MessageMap }>> = {
  ar: () => import('./messages/ar.json') as Promise<{ default: MessageMap }>,
  en: () => import('./messages/en.json') as Promise<{ default: MessageMap }>,
  ku: () => import('./messages/ku.json') as Promise<{ default: MessageMap }>,
};

const resolveLocale = (locale: string): SupportedLocale =>
  locale === 'en' || locale === 'ku' ? locale : 'ar';

const loadedMessages = new Map<SupportedLocale, MessageMap>();

const loadMessages = async (locale: SupportedLocale): Promise<MessageMap> => {
  const cached = loadedMessages.get(locale);
  if (cached) return cached;

  const { default: messages } = await messageLoaders[locale]();
  loadedMessages.set(locale, messages);

  return messages;
};

// Same key and default as getConfig() in OpenSooqi18n, which is what useLang() reads.
// index.tsx writes it during SSO init, before anything renders.
const readStoredLocale = (): SupportedLocale => {
  try {
    const stored = localStorage.getItem(I18N_CONFIG_KEY);
    if (stored) {
      const { selectedLang } = JSON.parse(stored) as { selectedLang?: string };
      return resolveLocale(selectedLang ?? 'ar');
    }
  } catch {
    // Fall through to the default below.
  }

  return 'ar';
};

// Start the download as main.js runs so it overlaps the SSO round trip that gates the
// first render. index.tsx normalises a 'ku' cookie to 'ar', so only 'en' picks a
// different file here.
loadMessages(Cookies.get('NEXT_LOCALE') === 'en' ? 'en' : 'ar').catch(() => {
  // The provider retries and surfaces the failure through its Suspense boundary.
});

// Lazy so the messages can be fetched: App.tsx already renders this inside a
// Suspense boundary that falls back to the splash screen.
const I18nProvider = lazy(async () => {
  const locale = readStoredLocale();
  const messages = await loadMessages(locale);

  const Localized: FC<WithChildren> = ({ children }) => (
    <IntlProvider locale={locale} messages={messages}>
      {children}
    </IntlProvider>
  );

  return { default: Localized };
});

export { I18nProvider };
